import React, { useRef, useCallback, useMemo, useEffect, useState } from "react";
import Select from "@components/ui/Select";
import ResourceCard from "@features/resources/components/ResourceCard";
import VStack from "@components/layout/VStack";
import HStack from "@components/layout/HStack";
import useScrollShadow from "@shared/hooks/useScrollShadow";
import { DeskLocationEntity, IFilterResource, ResourceEntity } from "@features/resources/services/types/resourceEntity";
import {
  useAppointmentStore,
  useOutlookStore,
  useResourceStore,
  useSettingStore,
  useThemeStore,
} from "@shared/stores";
import { Loader } from "@components/ui/Loader";
import { CategoryOperator, ComparisonOperator, SortBy, SortByValue } from "@shared/types/enums/globalEnum";
import SelectLocationForm from "@features/search/components/SelectLocationForm";
import SelectCategoryForm from "@features/search/components/SelectCategoryForm";
import Input from "@components/ui/Input";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MagnifyingGlass } from "@/pages/taskpane/components/icons/IconCustom";
import { paramsKey } from "@/pages/taskpane/shared/constants/paramsKey";
import {
  availableOptions,
  comparisonOperatorOptions,
  defaultPagination,
  sortByOptions,
} from "@/pages/taskpane/shared/constants/appConstants";
import { useFilterResources } from "../services/useFilterResources";
import { generateCId } from "@/pages/taskpane/lib/utils/randomUUID";
import { useScrollLoadMore } from "@/pages/taskpane/shared/hooks";
import EmptyData from "@/pages/taskpane/components/ui/EmptyData";

const ResourceSearch = () => {
  const { t } = useTranslation();
  const { isDarkMode } = useThemeStore();
  const { selectedResource, filterResource, setFilterResource, setDeskLocationList, isCheckSyncResource } = useResourceStore();
  const { defaultResourceType, defaultLocations, defaultCategoryIds, capacityFilter } = useSettingStore();
  const { startDateTimeUtc, endDateTimeUtc, timeZone } = useAppointmentStore();
  const { organizer, allAttendees, recurringDates, isNoEndDateRecurring, customUid, globalId } = useOutlookStore();
  const [searchParams] = useSearchParams();
  const listRef = useRef<HTMLDivElement | null>(null);
  const isFirstMountRef = useRef<boolean>(true);

  const [textDebounced, setTextDebounced] = useState(filterResource?.textSearch);
  useEffect(() => {
    if (isFirstMountRef.current) {
      isFirstMountRef.current = false;
      setTextDebounced(filterResource?.textSearch);
      return;
    }
    const id = setTimeout(() => {
      setTextDebounced(filterResource?.textSearch);
    }, 500);
    return () => clearTimeout(id);
  }, [filterResource?.textSearch]);

  const resourceType = useMemo(() => {
    return searchParams.get(paramsKey.resourceType) || "";
  }, [searchParams]);
  useEffect(() => {
    if (resourceType) {
      handleSaveFilterToRedux("resourceType", resourceType);
    }
  }, [resourceType]);

  const requestId = useMemo(() => {
    return generateCId();
  }, []);
  const payload = useMemo(
    () => ({
      pageSize: defaultPagination.pageSize,
      requestId: requestId,
      uid: customUid || "",
      globalId: globalId || "",
      organizer: organizer || "",
      resourceType: filterResource?.resourceType || "",
      locationIds: filterResource?.locations || [],
      timeZoneStandardName: timeZone || "",
      attendees: [organizer, ...allAttendees.map((att) => att.Email)],
      occurrences:
        recurringDates?.length > 0
          ? recurringDates.map((date) => ({
            utcStartDate: date.startDate || "",
            utcEndDate: date.endDate || "",
          }))
          : [
            {
              utcStartDate: startDateTimeUtc || "",
              utcEndDate: endDateTimeUtc || "",
            },
          ],
      isNoEndDate: recurringDates?.length > 0 ? isNoEndDateRecurring : null,
      isRecurring: recurringDates?.length > 0,
      isOnlyAvailable: filterResource?.status ?? true,
      name: textDebounced,
      sortBy: {
        field:
          filterResource?.sortBy === SortBy.CapacityAsc || filterResource?.sortBy === SortBy.CapacityDesc
            ? SortByValue.capacity
            : SortByValue.name,
        operator:
          filterResource?.sortBy === SortBy.NameDesc || filterResource?.sortBy === SortBy.CapacityDesc
            ? SortByValue.operatorDesc
            : SortByValue.operatorAsc,
      },
      filterCategory: {
        categoryIds: filterResource?.categories || [],
        operator: filterResource?.categoryOperator || CategoryOperator.Or,
      },
      filterCapacity: capacityFilter ? {
        capacity: filterResource?.capacityValue || 0,
        operator: filterResource?.capacityOperator || ComparisonOperator.MoreThan,
      } : null,
    }),
    [
      organizer,
      recurringDates,
      startDateTimeUtc,
      endDateTimeUtc,
      timeZone,
      allAttendees,
      isNoEndDateRecurring,
      customUid,
      globalId,
      textDebounced,
      filterResource,
    ]
  );

  const {
    data: resourcesDataInfinite,
    fetchNextPage,
    isFetching,
    isError,
  } = useFilterResources({
    payload,
    enabled: Boolean(filterResource && filterResource?.resourceType) && !isCheckSyncResource,
  });

  // Extract desk locations separately
  const extractedDeskLocations = useMemo(() => {
    const allDeskLocations = resourcesDataInfinite?.pages?.flatMap((page) => page.deskLocations) || [];
    const deskLocationList: DeskLocationEntity[] = [];
    allDeskLocations.forEach((location) => {
      if (!deskLocationList.some((item) => item.locationId === location.locationId)) {
        deskLocationList.push(location);
      }
    });
    return deskLocationList;
  }, [resourcesDataInfinite]);

  // Update store when desk locations change
  useEffect(() => {
    setDeskLocationList(extractedDeskLocations);
  }, [extractedDeskLocations, setDeskLocationList]);

  const resourceList = useMemo(() => {
    const allResources = resourcesDataInfinite?.pages?.flatMap((page) => page.resources) || [];
    return allResources.filter(
      (r: ResourceEntity) => !selectedResource.some((c) => c.id === r.id)
      // && !r.isDisableResourceByHotDeskRule(selectedResource, deskLocationList)
    );
  }, [resourcesDataInfinite, selectedResource]);

  const handleSaveFilterToRedux = (
    key: keyof IFilterResource,
    value: string | number | boolean | number[] | CategoryOperator | ComparisonOperator | SortBy,
    key2?: keyof IFilterResource,
    value2?: string | number | boolean | number[] | CategoryOperator | ComparisonOperator | SortBy
  ) => {
    setFilterResource((prev) => {
      const filterClone = prev
        ? { ...prev }
        : ({
          textSearch: "",
          status: true,
          capacityOperator: ComparisonOperator.MoreThan,
          capacityValue: 0,
          sortBy: SortBy.NameAsc,
          categories: [],
          categoryOperator: CategoryOperator.Or,
          locations: [],
          resourceType: "",
        } as IFilterResource);

      if (key === "locations") {
        filterClone.isChangedLocations = true;
      }
      if (key === "resourceType" && typeof value === "string") {
        const newResourceType = value;
        filterClone.resourceType = newResourceType;
        if (filterResource?.resourceType !== newResourceType && !filterClone.isChangedLocations) {
          const resourceTypeConfig = defaultLocations.defaultResourceTypes.find((i) => i.type === newResourceType);
          const newDefaultLocations = resourceTypeConfig?.locationIds
            ? resourceTypeConfig.locationIds.split(",").map(Number)
            : [];
          filterClone.locations = newDefaultLocations;
        }
      } else {
        (
          filterClone as Record<
            keyof IFilterResource,
            string | number | boolean | number[] | CategoryOperator | ComparisonOperator | SortBy
          >
        )[key] = value;
        if (key2 && value2) {
          (
            filterClone as Record<
              keyof IFilterResource,
              string | number | boolean | number[] | CategoryOperator | ComparisonOperator | SortBy
            >
          )[key2] = value2;
        }
      }
      return filterClone;
    });
  };

  useScrollLoadMore({
    containerRef: listRef,
    hasMore: !isError && !resourcesDataInfinite?.pages[resourcesDataInfinite?.pages?.length - 1]?.isLastPage,
    isLoading: isFetching,
    onLoadMore: () => {
      fetchNextPage();
    },
    threshold: 200,
  });

  const handleClearLocations = () => {
    handleSaveFilterToRedux("locations", []);
  };
  const handleApplyLocations = (selectedIds: number[]) => {
    handleSaveFilterToRedux("locations", selectedIds);
  };
  const LocationFormComponent = useCallback(
    (props: any) => (
      <SelectLocationForm
        {...props}
        locationSelected={filterResource?.locations || []}
        onClear={handleClearLocations}
        onApply={handleApplyLocations}
      />
    ),
    [filterResource?.locations]
  );

  const handleApplyCategories = (selectedIds: number[], operator?: CategoryOperator) => {
    handleSaveFilterToRedux("categories", selectedIds, "categoryOperator", operator);
  };
  const handleClearCategories = () => {
    handleSaveFilterToRedux("categories", [], "categoryOperator", CategoryOperator.Or);
  };
  const CategoryFormComponent = useCallback(
    (props: any) => (
      <SelectCategoryForm
        {...props}
        categoryOperator={filterResource?.categoryOperator || CategoryOperator.Or}
        categorySelected={filterResource?.categories || []}
        onClear={handleClearCategories}
        onApply={handleApplyCategories}
      />
    ),
    [filterResource?.categories, filterResource?.categoryOperator]
  );

  const placeholderCapacity = useMemo(() => {
    let text = t("Capacity");
    const operator = filterResource?.capacityOperator || ComparisonOperator.MoreThan;
    if (operator === ComparisonOperator.MoreThan) return text + ": > = ";
    if (operator === ComparisonOperator.Equal) return text + ": = ";
    if (operator === ComparisonOperator.LessThan) return text + ": < = ";
    return text;
  }, [filterResource?.capacityOperator, t]);

  //  Init filter resource khi lần đầu render
  useEffect(() => {
    if ((filterResource?.categories === null || filterResource?.categories === undefined) && defaultCategoryIds) {
      const defaultCategoryIdsArray = defaultCategoryIds?.split(",").map(Number) || [];
      handleSaveFilterToRedux("categories", defaultCategoryIdsArray, "categoryOperator", CategoryOperator.Or);
    }
    if (!filterResource?.resourceType && defaultResourceType) {
      handleSaveFilterToRedux("resourceType", defaultResourceType);
    }
  }, []);
  useScrollShadow(listRef);

  return (
    <VStack className="h-full overflow-hidden" gap={3}>
      <VStack className="w-full">
        {/* Search Input */}
        <Input
          placeholder={t("Search by resource name")}
          className="mb-2 h-[32px] text-[14px]"
          value={filterResource?.textSearch || ""}
          onChange={(e) => {
            handleSaveFilterToRedux("textSearch", e.target.value);
          }}
          leftIcon={<MagnifyingGlass isDarkMode={isDarkMode} size={20} />}
        />

        {/* Filter By Location */}

        <HStack className="w-full mb-2" justifyContent="between" gap={"[6px]"}>
          <Select
            placeholder={t("Where")}
            className="w-full text-xs h-[32px] bg-white dark:bg-darkBackgroundColor"
            Children={LocationFormComponent}
            isContentCenter
            maxHeightDropdown={400}
            isHighlightSelected={filterResource?.locations && filterResource?.locations?.length > 0}
            tooltipMessage={t("Filter by Location")}
            alignTo="left"
          />

          {/* Filter By Category */}

          <Select
            placeholder={t("Category")}
            className="w-full text-xs h-[32px] bg-white dark:bg-darkBackgroundColor"
            Children={CategoryFormComponent}
            isContentCenter
            maxHeightDropdown={400}
            isHighlightSelected={filterResource?.categories && filterResource?.categories?.length > 0}
            tooltipMessage={t("Filter by Category")}
            alignTo="right"
          />
        </HStack>

        <HStack className="w-full transition-[max-height,opacity] duration-300" gap={"[6px]"}>
          {capacityFilter ? <HStack className="flex-1 min-w-0" gap={"[6px]"}>
            {/* Filter By Capacity */}
            <Select
              placeholder={placeholderCapacity}
              className="w-[70%] text-xs h-[32px] bg-white dark:bg-darkBackgroundColor"
              options={comparisonOperatorOptions}
              value={filterResource?.capacityOperator || ComparisonOperator.MoreThan}
              onChange={(value) => {
                handleSaveFilterToRedux("capacityOperator", value as ComparisonOperator);
              }}
              isShowSelected={false}
              isHighlightSelected={true}
              tooltipMessage={t("Filter by Capacity")}
            />

            {/* Input Capacity Value */}
            <div className="flex-1">
              <input
                type="number"
                className="w-full px-2 h-[32px] text-xs rounded-[4px] outline-none bg-white border border-solid border-mediumGray dark:border-midGray dark:bg-darkBackgroundColor"
                value={filterResource?.capacityValue || 0}
                onChange={(e) => {
                  if (Number(e.target.value) >= 0) {
                    handleSaveFilterToRedux("capacityValue", Number(e.target.value));
                  }
                }}
              />
            </div>
          </HStack> : null
          }

          <HStack className="flex-1 min-w-0" gap={"[6px]"} justifyContent="space-between">
            {/* Filter By Status */}
            <Select
              placeholder={t("Available")}
              className="flex-1 min-w-0 text-xs h-[32px] bg-white dark:bg-darkBackgroundColor"
              isContentCenter
              widthDropdown={140}
              options={availableOptions}
              value={filterResource?.status ?? true}
              onChange={(value) => {
                handleSaveFilterToRedux("status", value as boolean);
              }}
              isHighlightSelected
              tooltipMessage={t("Filter by availability")}
            />

            {/* Filter By Sort By */}

            <Select
              placeholder={t("Sort By")}
              className="flex-1 min-w-0 text-xs h-[32px] bg-white dark:bg-darkBackgroundColor"
              isContentCenter
              widthDropdown={140}
              options={sortByOptions}
              value={filterResource?.sortBy || SortBy.NameAsc}
              onChange={(value) => {
                handleSaveFilterToRedux("sortBy", value as SortBy);
              }}
              isShowSelected={false}
              isHighlightSelected={true}
              tooltipMessage={t("Sort By")}
              alignTo="right"
            />
          </HStack>
        </HStack>
      </VStack>

      <VStack ref={listRef} className="flex-1 overflow-y-auto no-scrollbar" gap={2}>
        {resourceList?.length > 0 ? (
          resourceList.map((item) => <ResourceCard key={item.id} resource={item} />)
        ) : !isFetching && !isCheckSyncResource ? (
          <EmptyData content={t("No Resources available")} />
        ) : null}
        {isFetching || isCheckSyncResource ? (
          <HStack className="w-full p-4 justify-center items-center">
            <Loader texts={[t("Loading...")]} />
          </HStack>
        ) : null}
      </VStack>
    </VStack>
  );
};

export default ResourceSearch;
